/**
 * Standard paper sizes and reference objects
 */

// ISO A Series paper sizes (in inches)
export const ISO_A_SIZES = [
  {
    id: 'a0',
    name: 'A0',
    width: 33.11,
    height: 46.81,
    widthDisplay: '841mm',
    heightDisplay: '1189mm',
    standard: 'ISO',
    description: 'Base size for ISO A series, with an area of 1 square meter'
  },
  {
    id: 'a1',
    name: 'A1',
    width: 23.39,
    height: 33.11,
    widthDisplay: '594mm',
    heightDisplay: '841mm',
    standard: 'ISO',
    description: 'Half of A0, commonly used for large drawings and posters'
  },
  {
    id: 'a2',
    name: 'A2',
    width: 16.54,
    height: 23.39,
    widthDisplay: '420mm',
    heightDisplay: '594mm',
    standard: 'ISO',
    description: 'Half of A1, used for diagrams, charts, and layouts'
  },
  {
    id: 'a3',
    name: 'A3',
    width: 11.69,
    height: 16.54,
    widthDisplay: '297mm',
    heightDisplay: '420mm',
    standard: 'ISO',
    description: 'Half of A2, commonly used for larger documents and small posters'
  },
  {
    id: 'a4',
    name: 'A4',
    width: 8.27,
    height: 11.69,
    widthDisplay: '210mm',
    heightDisplay: '297mm',
    standard: 'ISO',
    description: 'Standard printer paper in most countries outside North America'
  },
  {
    id: 'a5',
    name: 'A5',
    width: 5.83,
    height: 8.27,
    widthDisplay: '148mm',
    heightDisplay: '210mm',
    standard: 'ISO',
    description: 'Half of A4, common for booklets, pamphlets, and notepads'
  },
  {
    id: 'a6',
    name: 'A6',
    width: 4.13,
    height: 5.83,
    widthDisplay: '105mm',
    heightDisplay: '148mm',
    standard: 'ISO',
    description: 'Half of A5, used for postcards, small notebooks, and pocket books'
  },
  {
    id: 'a7',
    name: 'A7',
    width: 2.91,
    height: 4.13,
    widthDisplay: '74mm',
    heightDisplay: '105mm',
    standard: 'ISO',
    description: 'Half of A6, used for small cards and small notepads'
  },
  {
    id: 'a8',
    name: 'A8',
    width: 2.05,
    height: 2.91,
    widthDisplay: '52mm',
    heightDisplay: '74mm',
    standard: 'ISO',
    description: 'Half of A7, often used for business cards in some countries'
  },
  {
    id: 'a9',
    name: 'A9',
    width: 1.46,
    height: 2.05,
    widthDisplay: '37mm',
    heightDisplay: '52mm',
    standard: 'ISO',
    description: 'Half of A8, used for labels and very small cards'
  },
  {
    id: 'a10',
    name: 'A10',
    width: 1.02,
    height: 1.46,
    widthDisplay: '26mm',
    heightDisplay: '37mm',
    standard: 'ISO',
    description: 'Half of A9, the smallest ISO A paper size'
  }
];

// ISO B Series paper sizes (in inches)
export const ISO_B_SIZES = [
  {
    id: 'b0',
    name: 'B0',
    width: 39.37,
    height: 55.67,
    widthDisplay: '1000mm',
    heightDisplay: '1414mm',
    standard: 'ISO',
    description: 'Largest ISO B series paper, used for large posters and artwork'
  },
  {
    id: 'b1',
    name: 'B1',
    width: 27.83,
    height: 39.37,
    widthDisplay: '707mm',
    heightDisplay: '1000mm',
    standard: 'ISO',
    description: 'Used for large artwork, posters, and displays'
  },
  {
    id: 'b2',
    name: 'B2',
    width: 19.69,
    height: 27.83,
    widthDisplay: '500mm',
    heightDisplay: '707mm',
    standard: 'ISO',
    description: 'Primarily used for posters and architectural/engineering drawings'
  },
  {
    id: 'b3',
    name: 'B3',
    width: 13.90,
    height: 19.69,
    widthDisplay: '353mm',
    heightDisplay: '500mm',
    standard: 'ISO',
    description: 'Often used for newspaper format or larger format books'
  },
  {
    id: 'b4',
    name: 'B4',
    width: 9.84,
    height: 13.90,
    widthDisplay: '250mm',
    heightDisplay: '353mm',
    standard: 'ISO',
    description: 'Commonly used for books, brochures and legal documents'
  },
  {
    id: 'b5',
    name: 'B5',
    width: 6.93,
    height: 9.84,
    widthDisplay: '176mm',
    heightDisplay: '250mm',
    standard: 'ISO',
    description: 'Popular size for books, especially in academic publishing'
  },
  {
    id: 'b6',
    name: 'B6',
    width: 4.92,
    height: 6.93,
    widthDisplay: '125mm',
    heightDisplay: '176mm',
    standard: 'ISO',
    description: 'Common for pocket books and small paperbacks'
  },
  {
    id: 'b7',
    name: 'B7',
    width: 3.46,
    height: 4.92,
    widthDisplay: '88mm',
    heightDisplay: '125mm',
    standard: 'ISO',
    description: 'Used for small booklets and mini-publications'
  },
  {
    id: 'b8',
    name: 'B8',
    width: 2.44,
    height: 3.46,
    widthDisplay: '62mm',
    heightDisplay: '88mm',
    standard: 'ISO',
    description: 'Used for small notepads and playing cards'
  }
];

// ISO C Series paper sizes (in inches) - primarily used for envelopes
export const ISO_C_SIZES = [
  {
    id: 'c0',
    name: 'C0',
    width: 36.10,
    height: 51.06,
    widthDisplay: '917mm',
    heightDisplay: '1297mm',
    standard: 'ISO',
    description: 'Size between A0 and B0, typically used for large envelopes'
  },
  {
    id: 'c1',
    name: 'C1',
    width: 25.51,
    height: 36.10,
    widthDisplay: '648mm',
    heightDisplay: '917mm',
    standard: 'ISO',
    description: 'Used for envelopes to contain A1 paper'
  },
  {
    id: 'c2',
    name: 'C2',
    width: 18.03,
    height: 25.51,
    widthDisplay: '458mm',
    heightDisplay: '648mm',
    standard: 'ISO',
    description: 'Used for envelopes to contain A2 paper'
  },
  {
    id: 'c3',
    name: 'C3',
    width: 12.76,
    height: 18.03,
    widthDisplay: '324mm',
    heightDisplay: '458mm',
    standard: 'ISO',
    description: 'Used for envelopes to contain A3 paper'
  },
  {
    id: 'c4',
    name: 'C4',
    width: 9.02,
    height: 12.76,
    widthDisplay: '229mm',
    heightDisplay: '324mm',
    standard: 'ISO',
    description: 'Commonly used envelope for A4 documents without folding'
  },
  {
    id: 'c5',
    name: 'C5',
    width: 6.38,
    height: 9.02,
    widthDisplay: '162mm',
    heightDisplay: '229mm',
    standard: 'ISO',
    description: 'Popular envelope size for A5 documents or folded A4'
  },
  {
    id: 'c6',
    name: 'C6',
    width: 4.49,
    height: 6.38,
    widthDisplay: '114mm',
    heightDisplay: '162mm',
    standard: 'ISO',
    description: 'Standard envelope for A6 or folded A5/A4 documents'
  },
  {
    id: 'c7',
    name: 'C7',
    width: 3.19,
    height: 4.49,
    widthDisplay: '81mm',
    heightDisplay: '114mm',
    standard: 'ISO',
    description: 'Small envelope for cards or folded A6'
  },
  {
    id: 'dl',
    name: 'DL',
    width: 4.33,
    height: 8.66,
    widthDisplay: '110mm',
    heightDisplay: '220mm',
    standard: 'ISO',
    description: 'Common business envelope size, fits A4 folded in thirds'
  }
];

// US Standard paper sizes (in inches)
export const US_SIZES = [
  {
    id: 'letter',
    name: 'Letter',
    width: 8.5,
    height: 11,
    widthDisplay: '8.5"',
    heightDisplay: '11"',
    standard: 'US',
    description: 'Standard US letter size, most common paper size in North America'
  },
  {
    id: 'legal',
    name: 'Legal',
    width: 8.5,
    height: 14,
    widthDisplay: '8.5"',
    heightDisplay: '14"',
    standard: 'US',
    description: 'Used for legal documents, contracts, and forms'
  },
  {
    id: 'tabloid',
    name: 'Tabloid/Ledger',
    width: 11,
    height: 17,
    widthDisplay: '11"',
    heightDisplay: '17"',
    standard: 'US',
    description: 'Called Tabloid in portrait, Ledger in landscape orientation'
  },
  {
    id: 'superb',
    name: 'Super B/A3+',
    width: 13,
    height: 19,
    widthDisplay: '13"',
    heightDisplay: '19"',
    standard: 'US',
    description: 'Common in digital printing, allows for full-bleed printing of tabloid size'
  },
  {
    id: 'statement',
    name: 'Statement',
    width: 5.5,
    height: 8.5,
    widthDisplay: '5.5"',
    heightDisplay: '8.5"',
    standard: 'US',
    description: 'Half of Letter size, often called Mini, Half Letter, or Memo'
  },
  {
    id: 'executive',
    name: 'Executive',
    width: 7.25,
    height: 10.5,
    widthDisplay: '7.25"',
    heightDisplay: '10.5"',
    standard: 'US',
    description: 'Slightly smaller than Letter, used for business correspondence'
  }
];

// ANSI Series paper sizes (in inches)
export const ANSI_SIZES = [
  {
    id: 'ansi-a',
    name: 'ANSI A',
    width: 8.5,
    height: 11,
    widthDisplay: '8.5"',
    heightDisplay: '11"',
    standard: 'ANSI',
    description: 'Same as Letter size, but in technical/engineering context'
  },
  {
    id: 'ansi-b',
    name: 'ANSI B',
    width: 11,
    height: 17,
    widthDisplay: '11"',
    heightDisplay: '17"',
    standard: 'ANSI',
    description: 'Same as Tabloid/Ledger size in technical/engineering context'
  },
  {
    id: 'ansi-c',
    name: 'ANSI C',
    width: 17,
    height: 22,
    widthDisplay: '17"',
    heightDisplay: '22"',
    standard: 'ANSI',
    description: 'Used for architectural and engineering drawings'
  },
  {
    id: 'ansi-d',
    name: 'ANSI D',
    width: 22,
    height: 34,
    widthDisplay: '22"',
    heightDisplay: '34"',
    standard: 'ANSI',
    description: 'Large format technical drawings and blueprints'
  },
  {
    id: 'ansi-e',
    name: 'ANSI E',
    width: 34,
    height: 44,
    widthDisplay: '34"',
    heightDisplay: '44"',
    standard: 'ANSI',
    description: 'Extra large format for engineering and architectural plans'
  }
];

// ARCH Series paper sizes (in inches)
export const ARCH_SIZES = [
  {
    id: 'arch-a',
    name: 'Arch A',
    width: 9,
    height: 12,
    widthDisplay: '9"',
    heightDisplay: '12"',
    standard: 'ARCH',
    description: 'Similar to ANSI A but with wider margins for architecture'
  },
  {
    id: 'arch-b',
    name: 'Arch B',
    width: 12,
    height: 18,
    widthDisplay: '12"',
    heightDisplay: '18"',
    standard: 'ARCH',
    description: 'Used for larger architectural drawings'
  },
  {
    id: 'arch-c',
    name: 'Arch C',
    width: 18,
    height: 24,
    widthDisplay: '18"',
    heightDisplay: '24"',
    standard: 'ARCH',
    description: 'Medium-sized architectural drawings and plans'
  },
  {
    id: 'arch-d',
    name: 'Arch D',
    width: 24,
    height: 36,
    widthDisplay: '24"',
    heightDisplay: '36"',
    standard: 'ARCH',
    description: 'Common size for construction blueprints'
  },
  {
    id: 'arch-e',
    name: 'Arch E',
    width: 36,
    height: 48,
    widthDisplay: '36"',
    heightDisplay: '48"',
    standard: 'ARCH',
    description: 'Largest standard architectural drawing format'
  },
  {
    id: 'arch-e1',
    name: 'Arch E1',
    width: 30,
    height: 42,
    widthDisplay: '30"',
    heightDisplay: '42"',
    standard: 'ARCH',
    description: 'Alternative to Arch E, more common in some practices'
  }
];

// JIS (Japanese Industrial Standard) B Series sizes (in inches)
export const JIS_SIZES = [
  {
    id: 'jis-b0',
    name: 'JIS B0',
    width: 40.55,
    height: 57.32,
    widthDisplay: '1030mm',
    heightDisplay: '1456mm',
    standard: 'JIS',
    description: 'Japanese standard, larger than ISO B0'
  },
  {
    id: 'jis-b1',
    name: 'JIS B1',
    width: 28.66,
    height: 40.55,
    widthDisplay: '728mm',
    heightDisplay: '1030mm',
    standard: 'JIS',
    description: 'Common for large posters in Japan'
  },
  {
    id: 'jis-b2',
    name: 'JIS B2',
    width: 20.28,
    height: 28.66,
    widthDisplay: '515mm',
    heightDisplay: '728mm',
    standard: 'JIS',
    description: 'Used for posters and large format artwork in Japan'
  },
  {
    id: 'jis-b3',
    name: 'JIS B3',
    width: 14.33,
    height: 20.28,
    widthDisplay: '364mm',
    heightDisplay: '515mm',
    standard: 'JIS',
    description: 'Often used for larger magazines and calendars in Japan'
  },
  {
    id: 'jis-b4',
    name: 'JIS B4',
    width: 10.12,
    height: 14.33,
    widthDisplay: '257mm',
    heightDisplay: '364mm',
    standard: 'JIS',
    description: 'Common in Japanese offices, larger than ISO B4'
  },
  {
    id: 'jis-b5',
    name: 'JIS B5',
    width: 7.17,
    height: 10.12,
    widthDisplay: '182mm',
    heightDisplay: '257mm',
    standard: 'JIS',
    description: 'Popular book and magazine size in Japan'
  },
  {
    id: 'jis-b6',
    name: 'JIS B6',
    width: 5.04,
    height: 7.17,
    widthDisplay: '128mm',
    heightDisplay: '182mm',
    standard: 'JIS',
    description: 'Common Japanese paperback book size'
  },
  {
    id: 'jis-b7',
    name: 'JIS B7',
    width: 3.58,
    height: 5.04,
    widthDisplay: '91mm',
    heightDisplay: '128mm',
    standard: 'JIS',
    description: 'Used for small books and notepads in Japan'
  }
];

// North American Envelope Sizes (in inches)
export const US_ENVELOPE_SIZES = [
  {
    id: 'comm-10',
    name: 'No. 10',
    width: 4.125,
    height: 9.5,
    widthDisplay: '4.125"',
    heightDisplay: '9.5"',
    standard: 'US',
    description: 'Standard business envelope in the US, fits letter paper folded in thirds'
  },
  {
    id: 'comm-9',
    name: 'No. 9',
    width: 3.875,
    height: 8.875,
    widthDisplay: '3.875"',
    heightDisplay: '8.875"',
    standard: 'US',
    description: 'Return envelope size that fits inside a No. 10 envelope'
  },
  {
    id: 'comm-6-3-4',
    name: 'No. 6¾',
    width: 3.625,
    height: 6.5,
    widthDisplay: '3.625"',
    heightDisplay: '6.5"',
    standard: 'US',
    description: 'Personal envelope size, fits check-sized documents'
  },
  {
    id: 'a2',
    name: 'A2',
    width: 4.375,
    height: 5.75,
    widthDisplay: '4.375"',
    heightDisplay: '5.75"',
    standard: 'US',
    description: 'Common for personal correspondence and greeting cards'
  },
  {
    id: 'a7',
    name: 'A7',
    width: 5.25,
    height: 7.25,
    widthDisplay: '5.25"',
    heightDisplay: '7.25"',
    standard: 'US',
    description: 'Invitation envelope, fits 5"×7" cards'
  },
  {
    id: 'monarch',
    name: 'Monarch',
    width: 3.875,
    height: 7.5,
    widthDisplay: '3.875"',
    heightDisplay: '7.5"',
    standard: 'US',
    description: 'Executive stationery envelope, also called "personal size"'
  },
  {
    id: 'catalog',
    name: 'Catalog',
    width: 9,
    height: 12,
    widthDisplay: '9"',
    heightDisplay: '12"',
    standard: 'US',
    description: 'For mailing unfolded documents, catalogs, and magazines'
  }
];

// Photo Print Sizes (in inches)
export const PHOTO_SIZES = [
  {
    id: 'photo-wallet',
    name: 'Wallet',
    width: 2.5,
    height: 3.5,
    widthDisplay: '2.5"',
    heightDisplay: '3.5"',
    standard: 'PHOTO',
    description: 'Small wallet-sized photo print'
  },
  {
    id: 'photo-4x6',
    name: '4×6',
    width: 4,
    height: 6,
    widthDisplay: '4"',
    heightDisplay: '6"',
    standard: 'PHOTO',
    description: 'Standard consumer photo print size'
  },
  {
    id: 'photo-5x7',
    name: '5×7',
    width: 5,
    height: 7,
    widthDisplay: '5"',
    heightDisplay: '7"',
    standard: 'PHOTO',
    description: 'Common photo size for framing'
  },
  {
    id: 'photo-8x10',
    name: '8×10',
    width: 8,
    height: 10,
    widthDisplay: '8"',
    heightDisplay: '10"',
    standard: 'PHOTO',
    description: 'Popular photo enlargement size'
  },
  {
    id: 'photo-11x14',
    name: '11×14',
    width: 11,
    height: 14,
    widthDisplay: '11"',
    heightDisplay: '14"',
    standard: 'PHOTO',
    description: 'Medium-large size for portraits and art prints'
  },
  {
    id: 'photo-16x20',
    name: '16×20',
    width: 16,
    height: 20,
    widthDisplay: '16"',
    heightDisplay: '20"',
    standard: 'PHOTO',
    description: 'Large format photo print, common for posters'
  },
  {
    id: 'photo-20x30',
    name: '20×30',
    width: 20,
    height: 30,
    widthDisplay: '20"',
    heightDisplay: '30"',
    standard: 'PHOTO',
    description: 'Extra large photo print size'
  },
  {
    id: 'photo-square-5x5',
    name: '5×5 Square',
    width: 5,
    height: 5,
    widthDisplay: '5"',
    heightDisplay: '5"',
    standard: 'PHOTO',
    description: 'Square format, popular for social media'
  },
  {
    id: 'photo-square-8x8',
    name: '8×8 Square',
    width: 8,
    height: 8,
    widthDisplay: '8"',
    heightDisplay: '8"',
    standard: 'PHOTO',
    description: 'Medium square format photo print'
  },
  {
    id: 'photo-panorama',
    name: 'Panorama',
    width: 12,
    height: 4,
    widthDisplay: '12"',
    heightDisplay: '4"',
    standard: 'PHOTO',
    description: 'Wide format for landscape photography'
  }
];

// Common reference objects (in inches)
export const COMMON_REFERENCE_OBJECTS = [
  {
    id: 'credit-card',
    name: 'Credit Card',
    width: 3.37,
    height: 2.125,
    widthDisplay: '3.37"',
    heightDisplay: '2.13"',
    description: 'Standard credit/debit card and ID card size (ISO/IEC 7810 ID-1)'
  },
  {
    id: 'business-card',
    name: 'Business Card (US)',
    width: 3.5,
    height: 2,
    widthDisplay: '3.5"',
    heightDisplay: '2"',
    description: 'Standard US business card size'
  },
  {
    id: 'business-card-eu',
    name: 'Business Card (EU)',
    width: 3.346,
    height: 2.165,
    widthDisplay: '85mm',
    heightDisplay: '55mm',
    description: 'Standard European business card size'
  },
  {
    id: 'dollar-bill',
    name: 'Dollar Bill',
    width: 6.14,
    height: 2.61,
    widthDisplay: '6.14"',
    heightDisplay: '2.61"',
    description: 'US paper currency (all denominations share the same size)'
  },
  {
    id: 'euro-bill',
    name: 'Euro (€5)',
    width: 4.7,
    height: 2.56,
    widthDisplay: '120mm',
    heightDisplay: '65mm',
    description: '5 Euro banknote (larger denominations are progressively larger)'
  },
  {
    id: 'iphone',
    name: 'iPhone 13',
    width: 2.82,
    height: 5.78,
    widthDisplay: '71.5mm',
    heightDisplay: '146.7mm',
    description: 'iPhone 13 dimensions (not including camera bump)'
  },
  {
    id: 'quarter',
    name: 'US Quarter',
    width: 0.955,
    height: 0.955,
    widthDisplay: '0.955"',
    heightDisplay: '0.955"',
    description: 'US 25-cent coin, diameter of 24.26mm'
  },
  {
    id: 'cd',
    name: 'CD/DVD',
    width: 4.724,
    height: 4.724,
    widthDisplay: '120mm',
    heightDisplay: '120mm',
    description: 'Standard compact disc or DVD diameter'
  },
  {
    id: 'polaroid',
    name: 'Polaroid Photo',
    width: 3.5,
    height: 4.2,
    widthDisplay: '3.5"',
    heightDisplay: '4.2"',
    description: 'Classic Polaroid 600 film image size (including border)'
  },
  {
    id: 'instagram',
    name: 'Instagram (Sq)',
    width: 3.0,
    height: 3.0,
    widthDisplay: '3.0"',
    heightDisplay: '3.0"',
    description: 'Instagram square photo format if printed at 300 PPI'
  }
];

// All paper sizes combined for selection purposes
export const ALL_PAPER_SIZES = [
  ...ISO_A_SIZES.map(size => ({ ...size, category: 'ISO A Series' })),
  ...ISO_B_SIZES.map(size => ({ ...size, category: 'ISO B Series' })),
  ...ISO_C_SIZES.map(size => ({ ...size, category: 'ISO C Series (Envelopes)' })),
  ...US_SIZES.map(size => ({ ...size, category: 'US Standard' })),
  ...ANSI_SIZES.map(size => ({ ...size, category: 'ANSI Series' })),
  ...ARCH_SIZES.map(size => ({ ...size, category: 'ARCH Series' })),
  ...JIS_SIZES.map(size => ({ ...size, category: 'JIS (Japanese)' })),
  ...US_ENVELOPE_SIZES.map(size => ({ ...size, category: 'US Envelopes' })),
  ...PHOTO_SIZES.map(size => ({ ...size, category: 'Photo Prints' }))
];